import { Expand } from 'lucide-react'
import React, { useState } from 'react'
import { Card, ImageDialog, Text } from '../ui'

/**
 * Props interface for diagram data
 */
export interface DiagramData {
  type: 'architecture' | 'flow' | 'sequence' | 'component'
  description: string
  imageUrl: string
}

/**
 * Props interface for the TechnicalDiagram component
 */
export interface TechnicalDiagramProps {
  /** The diagram data to display */
  diagram: DiagramData
  /** Additional CSS classes */
  className?: string
}

/**
 * Technical Diagram component for displaying architectural diagrams and flowcharts
 *
 * Features:
 * - Image-based diagram display
 * - Clickable to open full-screen dialog with zoom
 * - Mobile-optimized with touch gestures
 * - Responsive design with proper scaling
 * - Accessible with proper ARIA labels
 * - Professional styling with design system
 */
export const TechnicalDiagram: React.FC<TechnicalDiagramProps> = ({
  diagram,
  className = '',
}) => {
  const { type, description, imageUrl } = diagram
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  /**
   * Handle opening the full-screen dialog
   */
  const handleImageClick = () => {
    setIsDialogOpen(true)
  }

  /**
   * Handle closing the dialog
   */
  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  /**
   * Get diagram type label for accessibility
   */
  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      architecture: 'System Architecture Diagram',
      flow: 'Process Flow Diagram',
      sequence: 'Sequence Diagram',
      component: 'Component Diagram',
    }
    return labels[type] || 'Technical Diagram'
  }

  return (
    <div className={`technical-diagram ${className}`}>
      <Text size="sm" variant="secondary" className="diagram-description">
        {description}
      </Text>

      <Card variant="default" padding="md" className="diagram-container">
        <div
          className="image-diagram"
          onClick={handleImageClick}
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              handleImageClick()
            }
          }}
          aria-label={`Click to view ${getTypeLabel(type)} in full screen`}
          style={{
            cursor: 'pointer',
            position: 'relative',
          }}
        >
          <img
            src={imageUrl}
            alt={description}
            className="diagram-image"
            loading="lazy"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              maxWidth: '100%',
              transition: 'transform 0.2s ease, opacity 0.2s ease',
            }}
          />

          {/* Expand icon overlay */}
          <div
            className="diagram-expand-overlay"
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '50%',
              padding: '8px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: '0.8',
              transition: 'opacity 0.2s ease',
            }}
          >
            <Expand size={16} />
          </div>
        </div>
      </Card>

      {/* Full-screen Image Dialog */}
      <ImageDialog
        isOpen={isDialogOpen}
        imageUrl={imageUrl}
        imageAlt={description}
        title={getTypeLabel(type)}
        onClose={handleCloseDialog}
      />
    </div>
  )
}

export default TechnicalDiagram
